import React from "react";

import { map } from "lodash";
import { useForm } from "react-hook-form";

import { FieldErrors } from "src/api/types";
import { FileMapping } from "src/integrationNode/editorComponents/FileMapping";
import { InsightMappingRow } from "src/integrationNode/editorComponents/InsightMappingRow";
import { SubFormBaseProps } from "src/integrationNode/IntegrationNodeEditor";
import {
  DEFAULT_OUTPUT_BE_NAME,
  IntegrationResource,
  OutputMapping,
  OutputMappings as OutputMappingsType,
} from "src/integrationNode/types";
import { ManifestIntegrationResource } from "src/manifestConnectionNode/types";
import { useSubmitForm } from "src/utils/useSubmitForm";

type Props = {
  integrationResource: IntegrationResource | ManifestIntegrationResource;
  updateMappingOutput: (outputMapping: OutputMappingsType) => void;
  runFieldErrors?: FieldErrors;
} & SubFormBaseProps;

export const OutputMappings: React.FC<Props> = ({
  integrationResource,
  updateMappingOutput,
  runFieldErrors,
  immutable,
  isReactive,
}) => {
  const formProps = useForm({
    defaultValues: integrationResource.output,
    ...(isReactive && { values: integrationResource.output }),
  });

  useSubmitForm({
    onChange: (data: OutputMappingsType) => {
      updateMappingOutput(data);
    },
    disabled: isReactive,
    previousValues: integrationResource.output,
    watch: formProps.watch,
  });

  const defaultReportMapping =
    integrationResource.output[DEFAULT_OUTPUT_BE_NAME];

  const hasInsights =
    Object.keys(integrationResource.output.insights).length > 0;

  const renderInsights = () => (
    <>
      {map(integrationResource.output.insights, (outputMapping, beName) => (
        <InsightMappingRow
          key={beName}
          deprecationText={outputMapping.deprecation_text}
          displayName={outputMapping.displayName}
          fieldName={`insights.${beName}`}
          formProps={formProps}
          hint={outputMapping.hint}
          immutable={immutable}
          runFieldError={runFieldErrors?.[outputMapping.id]}
          type={outputMapping.type}
        />
      ))}
    </>
  );

  return (
    <>
      <InsightMappingRow
        description="Add the entire API response into the data object"
        displayName={defaultReportMapping.displayName}
        fieldName={DEFAULT_OUTPUT_BE_NAME}
        formProps={formProps}
        immutable={immutable}
        runFieldError={runFieldErrors?.[defaultReportMapping.id]}
      />
      {hasInsights && renderInsights()}
    </>
  );
};

type FileOutputMappingsProps = {
  files: { [key: string]: OutputMapping };
  updateMappingFiles: (filesMapping: { [key: string]: OutputMapping }) => void;
  runFieldErrors?: FieldErrors;
} & SubFormBaseProps;

export const FileOutputMappings: React.FC<FileOutputMappingsProps> = ({
  files,
  updateMappingFiles,
  runFieldErrors,
  immutable,
  isReactive,
}) => {
  const formProps = useForm<{ [key: string]: OutputMapping }>({
    defaultValues: files,
    ...(isReactive && { values: files }),
  });

  useSubmitForm({
    onChange: (data: { [key: string]: OutputMapping }) => {
      updateMappingFiles(data);
    },
    disabled: isReactive,
    previousValues: files,
    watch: formProps.watch,
  });

  return (
    <>
      {Object.entries(files || {}).map(([key, file]) => (
        <FileMapping
          key={key}
          description="Specify where to store the document reference for this file."
          displayName="Return document as file"
          fieldName={key}
          formProps={formProps}
          hint={
            file.hint ||
            "When enabled, the provider will return a document (e.g. PDF credit report) alongside the regular response data. You can then use this file within certain nodes on the platform (e.g. Manual Review, AI Node)."
          }
          immutable={immutable}
          runFieldError={runFieldErrors?.[key]}
        />
      ))}
    </>
  );
};
